import React, { useEffect } from 'react';

import { POLICIES } from '@vlabs/api-bindings/src/luna-client/handlers/constants';
import validate from '@vlabs/shared/validators';
import { Control } from '@vlabs/uikit';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { defaultHttpValues } from './defaultValues';

const headerLineRegex = /^[\w\-_]*: .*$/i;

export function HttpCallbackPolicyForm({ fieldName = undefined }: { fieldName?: string }): JSX.Element {
  const { t } = useTranslation();
  const prefix = fieldName ? `${fieldName}.` : '';
  const {
    unregister, setValue, register, formState, control, watch,
  } = useFormContext();
  const type = useWatch({ name: `${prefix}authorization.type` });
  const authorizationType = watch(`${prefix}authorization.type`);

  useEffect(() => {
    if (!type) {
      unregister(`${prefix}authorization`);
    }

    Object.entries(defaultHttpValues).forEach(([key, value]) => {
      setValue(`${prefix}${key}`, value);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <React.Fragment>
      <Control.Input
        id="url"
        label={t('monokit:entities.handlers.ui.forms.http callback policy form.url input.label') as unknown as string}
        placeholder={t('monokit:entities.handlers.ui.forms.http callback policy form.url input.placeholder') as unknown as string}
        {...register(`${prefix}url`, {
          required: validate.required(),
        })}
        autoFocus
        data-testid="callback.url"
        errors={formState.errors}
      />
      <Control.Select
        control={control}
        data-testid="callback.authorization.type"
        id="authorization.type"
        isClearable
        label={t('monokit:entities.handlers.ui.forms.http callback policy form.authorization type select.label') as unknown as string}
        name={`${prefix}authorization.type`}
        // @ts-expect-error lack of types POLICIES
        options={POLICIES.CALLBACK.authorization.type.raw}
      />
      {/* @ts-expect-error lack of types POLICIES */}
      {authorizationType?.value === POLICIES.CALLBACK.authorization.type.basic.value && (
        <React.Fragment>
          <Control.Input
            id="authorization.login"
            label={t('monokit:entities.handlers.ui.forms.http callback policy form.login input.label') as unknown as string}
            placeholder={t('monokit:entities.handlers.ui.forms.http callback policy form.login input.placeholder') as unknown as string}
            {...register(`${prefix}authorization.login`, {
              required: type && validate.required(),
            })}
            data-testid="callback.authorization.login"
            errors={formState.errors}
          />
          <Control.Input
            id="authorization.password"
            label={t('monokit:entities.handlers.ui.forms.http callback policy form.password input.label') as unknown as string}
            placeholder={t('monokit:entities.handlers.ui.forms.http callback policy form.password input.placeholder') as unknown as string}
            {...register(`${prefix}authorization.password`, {
              required: type && validate.required(),
            })}
            data-testid="callback.authorization.password"
            errors={formState.errors}
          />
        </React.Fragment>
      )}

      <Control.Input
        id="params.timeout"
        label={t('monokit:entities.handlers.ui.forms.http callback policy form.timeout input.label') as unknown as string}
        placeholder={t('monokit:entities.handlers.ui.forms.http callback policy form.timeout input.placeholder') as unknown as string}
        {...register(`${prefix}params.timeout`, {
          validate: validate.integer(),
        })}
        data-testid="callback.params.timeout"
        errors={formState.errors}
      />
      <Control.Select
        control={control}
        data-testid="callback.params.content_type"
        errors={formState.errors}
        id="params.content_type"
        label={t('monokit:entities.handlers.ui.forms.http callback policy form.content type select.label') as unknown as string}
        name={`${prefix}params.content_type`}
        // @ts-expect-error lack of types POLICIES
        options={POLICIES.CALLBACK.params.content_type.raw}
        // @ts-expect-error lack of types POLICIES
        value={POLICIES.CALLBACK.params.content_type['application/json']}
      />
      <Control.Textarea
        id="params.headers"
        label={t('monokit:entities.handlers.ui.forms.http callback policy form.headers text area.label') as unknown as string}
        placeholder={t('monokit:entities.handlers.ui.forms.http callback policy form.headers text area.placeholder') as unknown as string}
        {...register(`${prefix}params.headers`, {
          validate: (value: string): true | string => {
            if (value.length === 0) return true;
            const isNonValidIndex = value
              .split('\n')
              .map((v: string) => headerLineRegex.exec(v))
              .findIndex((v: RegExpExecArray | null) => v === null);
            if (isNonValidIndex !== -1) return t('monokit:entities.handlers.ui.forms.http callback policy form.headers text area.validation message') as unknown as string;
            return true;
          },
        })}
        data-testid="callback.params.headers"
        errors={formState.errors}
      />
    </React.Fragment>
  );
}
